import { monetaryValuesEqual } from "@common/helpers/monetary";
import {
    ScheduleMatchResolution,
    ScheduleLinkedApplication,
    scheduleSourceMoveSchema,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import { z } from "zod";
import {
    ScheduleMatchResolutionKind,
    ScheduleSourceType,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";
import {
    ScheduleDocumentLink,
    ScheduleDocumentType,
    documentIdOf,
    isLinkCoveredByApplication,
    mergeDocumentLinks,
} from "./scheduleDocumentLinks";
import { isResolutionCovering } from "./scheduleMatchConflicts";

export const SCHEDULE_SOURCE_MOVE_REFUSAL_MESSAGE =
    "Remove the schedule from the payment before matching this amount.";

export const SCHEDULE_SOURCE_MOVE_CONFIRM_TITLE =
    "This payment already has a schedule";

export const SCHEDULE_SOURCE_MOVE_CONFIRM_MESSAGE = `${SCHEDULE_SOURCE_MOVE_CONFIRM_TITLE}.`;

export const SCHEDULE_SOURCE_MOVE_CHANGED_MESSAGE =
    "This schedule changed since you opened this; review and try again";

export enum ScheduleSourceMoveRefusal {
    severalSchedules = "several_schedules",
    crossEntity = "cross_entity",
    templateType = "template_type",
    amountMismatch = "amount_mismatch",
    severalDocuments = "several_documents",
}

export interface ScheduleSourceMoveApplication extends ScheduleLinkedApplication {
    scheduleType: ScheduleType;
    /** A rule-suggested draft still owns the source for the unique index. */
    isDraft: boolean;
    entityId: number;
}

export type ScheduleSourceMove = z.infer<typeof scheduleSourceMoveSchema>;

export type ScheduleSourceMoveClassification =
    | { kind: "none" }
    | { kind: "move"; moves: ScheduleSourceMove[] }
    | { kind: "refuse"; reason: ScheduleSourceMoveRefusal };

const REFUSAL_PRIORITY = [
    ScheduleSourceMoveRefusal.severalSchedules,
    ScheduleSourceMoveRefusal.crossEntity,
    ScheduleSourceMoveRefusal.templateType,
    ScheduleSourceMoveRefusal.amountMismatch,
    ScheduleSourceMoveRefusal.severalDocuments,
];

function scheduleTypeFitsDocument(options: {
    scheduleType: ScheduleType;
    documentType: ScheduleDocumentType;
}): boolean {
    if (options.documentType === ScheduleSourceType.bill) {
        return options.scheduleType === ScheduleType.expense;
    }
    return options.scheduleType === ScheduleType.revenue;
}

function highestRefusal(
    reasons: ScheduleSourceMoveRefusal[],
): ScheduleSourceMoveRefusal | null {
    return REFUSAL_PRIORITY.find((reason) => reasons.includes(reason)) ?? null;
}

/**
 * A payment schedule can move onto the document it is about to pay when that
 * document's paid lines have no schedule of their own. Both-sides links stay
 * conflicts. A one-sided link that cannot move cleanly refuses the match.
 */
export function classifyScheduleSourceMoves(options: {
    links: ScheduleDocumentLink[];
    applications: ScheduleSourceMoveApplication[];
    documentType: ScheduleDocumentType;
    /** Document id → owning entity. A missing id refuses the move. */
    documentEntityIdByDocumentId: ReadonlyMap<string, number>;
}): ScheduleSourceMoveClassification {
    const transactionApplications = new Map<
        number,
        ScheduleSourceMoveApplication[]
    >();
    for (const application of options.applications) {
        if (
            application.sourceType !== ScheduleSourceType.transaction ||
            application.transactionId === null
        ) {
            continue;
        }
        const existing =
            transactionApplications.get(application.transactionId) ?? [];
        existing.push(application);
        transactionApplications.set(application.transactionId, existing);
    }
    const documentApplications = options.applications.filter(
        (application) => application.sourceType === options.documentType,
    );

    const moves: ScheduleSourceMove[] = [];
    const reasons: ScheduleSourceMoveRefusal[] = [];

    for (const link of mergeDocumentLinks(options.links)) {
        const applicationsOnPayment =
            transactionApplications.get(link.transactionId) ?? [];
        if (applicationsOnPayment.length === 0) {
            continue;
        }
        const coveringApplications = documentApplications.filter(
            (application) =>
                isLinkCoveredByApplication({
                    application,
                    applicationDocumentId: documentIdOf(
                        application,
                        options.documentType,
                    ),
                    link,
                }),
        );
        if (coveringApplications.some((application) => !application.isDraft)) {
            continue;
        }
        if (coveringApplications.some((application) => application.isDraft)) {
            reasons.push(ScheduleSourceMoveRefusal.severalSchedules);
            continue;
        }
        const documentEntityId = options.documentEntityIdByDocumentId.get(
            link.documentId,
        );
        if (
            documentEntityId === undefined ||
            applicationsOnPayment.some(
                (application) => application.entityId !== documentEntityId,
            )
        ) {
            reasons.push(ScheduleSourceMoveRefusal.crossEntity);
            continue;
        }
        if (applicationsOnPayment.length !== 1) {
            reasons.push(ScheduleSourceMoveRefusal.severalSchedules);
            continue;
        }
        const application = applicationsOnPayment[0];
        if (
            !scheduleTypeFitsDocument({
                scheduleType: application.scheduleType,
                documentType: options.documentType,
            })
        ) {
            reasons.push(ScheduleSourceMoveRefusal.templateType);
            continue;
        }
        if (
            application.amount === null ||
            !monetaryValuesEqual(application.amount, link.amount)
        ) {
            reasons.push(ScheduleSourceMoveRefusal.amountMismatch);
            continue;
        }
        moves.push({
            applicationId: application.applicationId,
            transactionId: link.transactionId,
            documentId: link.documentId,
            documentType: options.documentType,
            lineItemIds: link.lineItemIds,
            amount: link.amount,
            scheduleName: application.scheduleName,
        });
    }

    return finalizeScheduleSourceMoves({ moves, reasons });
}

/** Bill and invoice classifications of one match. A refusal on either side wins. */
export function combineScheduleSourceMoveClassifications(
    classifications: ScheduleSourceMoveClassification[],
): ScheduleSourceMoveClassification {
    const reasons = classifications.flatMap((classification) =>
        classification.kind === "refuse" ? [classification.reason] : [],
    );
    const moves = classifications.flatMap((classification) =>
        classification.kind === "move" ? classification.moves : [],
    );
    return finalizeScheduleSourceMoves({ moves, reasons });
}

function finalizeScheduleSourceMoves(options: {
    moves: ScheduleSourceMove[];
    reasons: ScheduleSourceMoveRefusal[];
}): ScheduleSourceMoveClassification {
    const reasons = [...options.reasons];
    if (movesShareDocument(options.moves)) {
        reasons.push(ScheduleSourceMoveRefusal.severalSchedules);
    }
    if (new Set(options.moves.map((move) => move.documentId)).size > 1) {
        reasons.push(ScheduleSourceMoveRefusal.severalDocuments);
    }
    const reason = highestRefusal(reasons);
    if (reason) {
        return { kind: "refuse", reason };
    }
    if (options.moves.length === 0) {
        return { kind: "none" };
    }
    return { kind: "move", moves: options.moves };
}

function movesShareDocument(moves: ScheduleSourceMove[]): boolean {
    const seenDocumentIds = new Set<string>();
    for (const move of moves) {
        if (seenDocumentIds.has(move.documentId)) {
            return true;
        }
        seenDocumentIds.add(move.documentId);
    }
    return false;
}

/** The moves that ride one transaction's own approve step. */
export function scheduleSourceMovesForTransaction(options: {
    moves: ScheduleSourceMove[];
    transactionId: number;
}): ScheduleSourceMove[] {
    return options.moves.filter(
        (move) => move.transactionId === options.transactionId,
    );
}

const MOVE_CONFIRMING_RESOLUTION_KINDS = [
    ScheduleMatchResolutionKind.moveToDocument,
    ScheduleMatchResolutionKind.keepTransactions,
];

/**
 * Moves the request has not confirmed. Keeping the payment's schedule in a
 * both-sides conflict confirms its move as well: the document's schedule goes
 * away, so the payment's has to land there. `invoiceId` on the resolution is
 * the document id for a bill as well as an invoice.
 */
export function uncoveredScheduleSourceMoves(options: {
    moves: ScheduleSourceMove[];
    resolutions: ScheduleMatchResolution[];
}): ScheduleSourceMove[] {
    return options.moves.filter(
        (move) =>
            !options.resolutions.some((resolution) =>
                MOVE_CONFIRMING_RESOLUTION_KINDS.some((kind) =>
                    isResolutionCovering({
                        resolution,
                        kind,
                        documentId: move.documentId,
                        transactionId: move.transactionId,
                    }),
                ),
            ),
    );
}
